// DeleteAccountSettings — "Radera mitt konto" in Kontoinställningar. GDPR art. 17.
//
// The deletion is a HARD delete: profile, applications, uploaded CV, course
// progress and notifications are erased, the CV leaves object storage, and there
// is no soft-delete, no recovery window and no undo. So the design question
// here is not "how do we offer this" but "how do we make it impossible to do by
// accident" — three deliberate acts, none of which can be triggered by a stray
// click or a held Enter key:
//
//   1. Open the panel   — the destructive control is not on screen by default.
//   2. Open the dialog  — "Radera mitt konto" only reveals the confirmation.
//   3. Type the phrase  — the final button stays disabled until the user has
//                         typed RADERA exactly. No amount of clicking gets past
//                         a control that is disabled until a keyboard produced
//                         six specific characters.
//
// Rejected: a plain window.confirm (one keystroke away from destruction, and
// unstyleable), a "hold to delete" button (fun, but a stuck mouse button
// completes it), and a countdown that auto-enables (time is not intent).
//
// Employers and admins never see this at all — deleting an employer cascades
// into the organisation's job postings and every candidate's application to
// them, so the API refuses it with 403 and the UI must not dangle it.
import React, { useState } from 'react';
import { Trash2, AlertTriangle, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { accountApi } from '@/lib/api/accountApi';
import { IconChip, ActionButton } from '@/components/custom-ui';
import { Input } from '@/components/ui/input';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';

// Swedish, uppercase, and NOT the user's email: an email address is sitting in
// the browser's autofill and in the page above it, which makes it a phrase the
// user can produce without reading anything.
const CONFIRM_PHRASE = 'RADERA';

const WHAT_GOES = [
    'Din profil och dina kontouppgifter',
    'Ditt CV och andra uppladdade filer',
    'Alla dina jobbansökningar',
    'Dina kursresultat och genomförda utbildningar',
    'Dina bevakningar och notiser',
];

export const DeleteAccountSettings: React.FC = () => {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const [phrase, setPhrase] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Gate 0 — role. The API refuses employers and admins with 403 regardless;
    // this stops the control from ever being rendered for them, so nobody has to
    // discover the rule by triggering it.
    if (user?.role !== 'candidate') return null;

    const confirmed = phrase.trim().toUpperCase() === CONFIRM_PHRASE;

    const close = () => {
        if (deleting) return; // never yank the dialog out from under a live request
        setOpen(false);
        setPhrase('');
        setError(null);
    };

    const handleDelete = async () => {
        // Re-checked here and not just on the button: a disabled attribute is a
        // hint to the pointer, not a guarantee about how this function is reached.
        if (!confirmed || deleting) return;
        setDeleting(true);
        setError(null);
        try {
            await accountApi.deleteOwnAccount();
            // The account is gone, so the session must go with it — the token is
            // still syntactically valid and would otherwise sit in localStorage
            // producing 404s on every screen. logout() clears tokens, drops the
            // user from context and navigates; '/' is the public landing page,
            // which is the only place a deleted user still belongs.
            logout('/');
        } catch (err: unknown) {
            const e = err as { status?: number };
            // 404 = already deleted. The user asked for the account to be gone and
            // it is gone; showing them an error would be a lie about the outcome.
            if (e?.status === 404) {
                logout('/');
                return;
            }
            setError(
                e?.status === 403
                    ? 'Den här kontotypen kan inte raderas härifrån. Kontakta din administratör.'
                    : 'Kunde inte radera kontot. Försök igen om en stund.',
            );
            setDeleting(false);
        }
    };

    return (
        <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-6 border-red-100`}>
            <div className="flex items-center gap-3 mb-4">
                <IconChip icon={Trash2} size="lg" tone="red" />
                <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900">Radera konto</h2>
                    <p className="text-sm text-gray-500">Ta bort ditt konto och all din data permanent</p>
                </div>
            </div>

            {!open ? (
                <>
                    <p className="text-sm text-gray-600 mb-4">
                        Du kan när som helst radera ditt konto. Allt tas bort permanent och kan{' '}
                        <strong>inte</strong> återställas.
                    </p>
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="text-sm font-medium text-red-600 hover:text-red-700 hover:underline"
                    >
                        Radera mitt konto
                    </button>
                </>
            ) : (
                <div className="rounded-xl border border-red-200 bg-red-50/60 p-4">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-red-900">
                                Det här går inte att ångra
                            </p>
                            <p className="text-sm text-red-800 mt-1">Följande raderas permanent:</p>
                            <ul className="mt-2 space-y-1">
                                {WHAT_GOES.map((item) => (
                                    <li key={item} className="text-sm text-red-800 flex gap-2">
                                        <span aria-hidden="true">•</span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>

                            <label
                                htmlFor="delete-confirm"
                                className="block text-sm text-red-900 mt-4 mb-1.5"
                            >
                                Skriv <strong>{CONFIRM_PHRASE}</strong> för att bekräfta
                            </label>
                            <Input
                                id="delete-confirm"
                                value={phrase}
                                onChange={(e) => setPhrase(e.target.value)}
                                placeholder={CONFIRM_PHRASE}
                                autoComplete="off"
                                autoCorrect="off"
                                spellCheck={false}
                                disabled={deleting}
                                className="max-w-[220px] bg-white"
                            />

                            {error && <p className="text-sm text-red-700 mt-3">{error}</p>}

                            <div className="flex items-center gap-3 mt-4">
                                <ActionButton
                                    type="button"
                                    icon={Trash2}
                                    loading={deleting}
                                    disabled={!confirmed || deleting}
                                    onClick={handleDelete}
                                    className="bg-red-600 hover:bg-red-700"
                                >
                                    {deleting ? 'Raderar…' : 'Radera permanent'}
                                </ActionButton>
                                <button
                                    type="button"
                                    onClick={close}
                                    disabled={deleting}
                                    className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
                                >
                                    <X className="w-4 h-4" />
                                    Avbryt
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeleteAccountSettings;
